import {parseGraphhopperPoints} from '../graphhopperutil.js';
import {LineString} from 'ol/geom';

/**
 * @typedef {import("ol/geom").LineString} LineString
 */

const searchParams = new URLSearchParams(document.location.search);
function defaultNumber(param, fallback) {
  const value = searchParams.has(param) ? Number.parseFloat(searchParams.get(param)) : fallback;
  console.log('param', param, value);
  return value;
}

// Number of parts to split into when matching fails.
const PARTITIONS = 2;
// Maximum number of recursive partions to apply.
const DEPTH = 4;
// Threshold of part length above which stronger simplification is applied.
const THRESHOLD = defaultNumber('match_thresold', 1000); // [m]
// Douglas Peuker simplification tolerance (epsilon).
const TOLERANCE1 = defaultNumber('match_tolerance1', 0); // [m]
// Douglas Peuker simplification tolerance (epsilon).
const TOLERANCE2 = defaultNumber('match_tolerance2', 5); // [m]
// Maxium ratio a line car increase/reduce. Line above this ratio are considered failed.
const MAX_STRETCH = defaultNumber('match_stretch', 5) / 100;
// GPS accuracy (mapmatching is faster with smaller values).
const GPS_ACCURACY = defaultNumber('match_gps_accuracy', 40); // [m]
// Start with partitions of at most X coordinates
const MAX_INITIAL_POINTS = defaultNumber('match_max_initial_points', 0);

/**
 * Match a track using graphhopper.
 *
 * @param {LineString} lineString // in EPSG:21781
 * @param {string} graphhopperUrl
 * @param {string} profile
 * @returns {Promise<LineString>}
 */
export async function matchBestEffort(lineString, graphhopperUrl, profile) {
  // Restrict geom type
  if (lineString.getType() !== 'LineString') {
    console.error('Geometry is not of type `LineString`:', lineString.getType());
    throw new Error('Geometry is not of type `LineString`');
  }

  const params = {
    gps_accuracy: GPS_ACCURACY,
    profile: `schm${profile}`,
    locale: 'fr-CH',
    elevation: 'true',
    way_point_max_distance: '0',
    instructions: 'false',
    points_encoded: 'true'
  };
  const urlParams = new URLSearchParams(params).toString();
  const mapmatchingURL = `${graphhopperUrl}/match?${urlParams}`;
  // Begin recursive matching with partitions of less than MAX_INITIAL_POINTS coordinates
  // or the full initial line
  const parts = MAX_INITIAL_POINTS ?
    Math.floor(lineString.getCoordinates().length / MAX_INITIAL_POINTS) + 1 :
    1;
  return await recursivelyMatchBestEffort(lineString, parts, mapmatchingURL);
}


/**
 * Recursively splits routes int smaller parts to match partially if there is a matching error.
 *
 * For `PARTITIONS = 2` the maximum amount of request is `2^(DEPTH + 1) - 1`
 *
 * @param {LineString} lineString // in EPSG:21781
 * @param {number} parts
 * @param {number} depth
 * @param {string} mapmatchingURL
 * @returns {Promise<LineString>}
 */
async function recursivelyMatchBestEffort(lineString, parts, mapmatchingURL, depth = 1) {

  const coordinates = lineString.getCoordinates();

  // If part we surpass a given `DEPTH` we stop partitioning
  if (coordinates.length <= parts || depth > DEPTH) {
    return lineString;
  }

  let matched = [];
  const size = coordinates.length / parts;

  for (let i = 0; i < parts; i++) {
    // Slice coordinates
    const start = (i == 0) ? 0 : parseInt(i * size);
    const end = (i == parts - 1) ? coordinates.length : parseInt((i + 1) * size);
    const slice = coordinates.slice(start, end);

    // Simplify
    const sliceGeom = new LineString(slice);
    const sliceLength = sliceGeom.getLength();
    const tolerance = sliceLength > THRESHOLD ? TOLERANCE2 : TOLERANCE1;
    const simplified = tolerance ? sliceGeom.simplify(tolerance) : sliceGeom;

    // Call graphhopper
    const match = await fetchRoute(simplified, mapmatchingURL)
      .then(response => response.json())
      .then(async body => {
        if (body.map_matching) {
          // v6 error case
          const originalDistance = body.map_matching.original_distance;
          const distance = body.map_matching.distance;
          if (distance === 0 && originalDistance !== 0) {
            return await recursivelyMatchBestEffort(sliceGeom, PARTITIONS, mapmatchingURL, depth + 1);
          }
        }
        if (body.hasOwnProperty("message") && body["message"].includes('Sequence is broken for submitted track')) {
          // v3 error case
          return await recursivelyMatchBestEffort(sliceGeom, PARTITIONS, mapmatchingURL, depth + 1);
        }
        const result = extract_coordinates(body);
        // If length difference between original and matched part exceeds x% we discard the match
        const resultLength = result.getLength();
        const ratio = Math.abs(resultLength - sliceLength) / sliceLength;
        if (ratio > MAX_STRETCH) {
          console.log("bad result", JSON.stringify(body));
          return await recursivelyMatchBestEffort(sliceGeom, PARTITIONS, mapmatchingURL, depth + 1);
        } else {
          return result;
        }
      }).catch(e => {
        console.log(e);
        return sliceGeom;
      });

    matched = [...matched, ...match.getCoordinates()];
  }

  return new LineString(matched);
}


/**
 * Match a route with graphhopper.
 *
 * @param {LineString} lineString // in EPSG:21781
 * @param {string} mapmatchingURL
 * @returns {Promise<Response>}
 */
async function fetchRoute(lineString, mapmatchingURL) {
  // make a deep copy
  const input = lineString.clone();

  input.transform('EPSG:21781', 'EPSG:4326');
  const gpx = `
    <gpx>
      <trk>
        <trkseg>
        ${input.getCoordinates().map(coo => (`<trkpt lat="${coo[1]}" lon="${coo[0]}"></trkpt>`)).join("\n")}
        </trkseg>
      </trk>
    </gpx>`;

  return fetch(mapmatchingURL, {
    body: gpx,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/gpx+xml'
    },
    method: 'POST',
  });
}


/**
 * Extract coordinates from graphhopper response body and transform to EPSG:21781
 *
 * @param {Object} jsonBody
 * @returns {LineString}
 */
function extract_coordinates(jsonBody) {
  const points = jsonBody['paths'][0]['points'];
  const route = parseGraphhopperPoints(points, true, 'EPSG:21781');
  return route;
}
